import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";

import DataTable from "../components/DataTable";
import IntegrationsSection from "../components/IntegrationsSection";
import ScopeGate from "../components/ScopeGate";
import StatCard from "../components/StatCard";
import WriteOpsHint from "../components/WriteOpsHint";
import SecretRow from "../components/SecretRow";
import {
  fetchIntegrationCatalog,
  fetchMyRealmConfig,
  fetchMyRealmSourceAdapters,
  fetchRealmEmailChannel,
  fetchRealmGraphStats,
  fetchRealmMembers,
  fetchRealmSecretNames,
} from "../lib/api";
import { realmSecretNamesForDisplay } from "../lib/realmConfigSecrets";
import { canAdminRealm } from "../lib/scopes";
import type {
  EmailChannel,
  GraphStats,
  IntegrationType,
  Member,
  SourceAdapter,
} from "../lib/types";
import { INTEGRATIONS_WRITE_OPS } from "../writeops/integrations";
import { REALMS_WRITE_OPS } from "../writeops/realms";
import { useRealmContext } from "./RealmLayout";

const MEMBER_COLS: ColumnDef<Member, any>[] = [
  { header: "Subject", accessorKey: "subject" },
  { header: "Role", accessorKey: "role" },
];

export default function RealmOverviewRoute() {
  const { realmId, realm } = useRealmContext();
  const [members, setMembers] = useState<Member[]>([]);
  const [stats, setStats] = useState<GraphStats | null>(null);
  const [config, setConfig] = useState<Record<string, unknown> | null>(null);
  const [secretNames, setSecretNames] = useState<string[]>([]);
  const [adapters, setAdapters] = useState<SourceAdapter[] | null>(null);
  const [catalog, setCatalog] = useState<IntegrationType[] | null>(null);
  const [emailChannel, setEmailChannel] = useState<EmailChannel | null>(null);
  // Getrennt vom `null` oben: "nicht einsehbar" ist kein "nicht gebunden".
  const [emailUnreadable, setEmailUnreadable] = useState<boolean>(false);
  // Names also surface from `config.adapters[].credential_ref`, which `/v1/me`
  // serves to any member — so the list alone is no admission that the caller may
  // write them; the gate below decides that.
  const displaySecretNames = realmSecretNamesForDisplay(config, secretNames);

  useEffect(() => {
    if (!realmId) return;
    let live = true;
    // Each of these is optional context, not the page — a caller without the
    // operator bearer sees the realm without them rather than an error page.
    void (async () => {
      const [m, s, cfg, secs, adp, cat, mail] = await Promise.all([
        fetchRealmMembers(realmId).catch(() => ({ realm_id: realmId, members: [] })),
        fetchRealmGraphStats(realmId).catch(() => null),
        fetchMyRealmConfig(realmId).catch(() => null),
        fetchRealmSecretNames(realmId).catch(() => [] as string[]),
        fetchMyRealmSourceAdapters(realmId).catch(() => null),
        fetchIntegrationCatalog().catch(() => null),
        // 404 = nicht gebunden, alles andere (403) = nicht einsehbar. Die zwei
        // dürfen nicht in denselben Falsy-Zweig fallen (D-GOV-25).
        fetchRealmEmailChannel(realmId).then(
          (c) => ({ channel: c, unreadable: false }),
          (e: unknown) => ({
            channel: null,
            unreadable: (e as { status?: number })?.status !== 404,
          }),
        ),
      ]);
      if (!live) return;
      setMembers(m.members);
      setStats(s);
      setConfig(cfg ? cfg.config : null);
      setSecretNames(secs);
      setAdapters(adp);
      setCatalog(cat);
      setEmailChannel(mail.channel);
      setEmailUnreadable(mail.unreadable);
    })();
    return () => {
      live = false;
    };
  }, [realmId]);

  return (
    <div className="space-y-6">
      {realm && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Kind" value={realm.kind} />
          <StatCard label="Visibility" value={realm.visibility} />
          <StatCard
            label="Packs"
            value={realm.pack_bindings.length}
            hint={realm.pack_bindings.join(", ") || "—"}
          />
          <StatCard label="Members" value={members.length} />
        </div>
      )}

      {stats && (
        <div className="space-y-2">
          <h3 className="text-sm uppercase tracking-wide text-kw-text-muted">Graph Stats</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Entities" value={stats.total_entities} />
            <StatCard label="Edges" value={stats.total_edges} />
            <StatCard label="new 24h" value={stats.new_entities_24h} />
            <StatCard label="new 7d" value={stats.new_entities_7d} />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-sm uppercase tracking-wide text-kw-text-muted">Members</h3>
        <DataTable
          columns={MEMBER_COLS}
          data={members}
          emptyHint="No members — single-owner realm, or the member list needs the operator bearer."
        />
      </div>

      <IntegrationsSection
        adapters={adapters}
        catalog={catalog}
        emailChannel={emailChannel}
        emailChannelUnreadable={emailUnreadable}
        teamsChannel={
          ((config?.channels as Record<string, unknown> | undefined)?.teams as
            | Record<string, unknown>
            | undefined) ?? null
        }
      />

      <WriteOpsHint title="Integrationen ändern" ops={INTEGRATIONS_WRITE_OPS} />

      <details className="space-y-2">
        <summary className="text-sm uppercase tracking-wide text-kw-text-muted cursor-pointer">
          Rohe Realm-Config
        </summary>
        <pre className="mt-2 text-xs font-mono bg-kw-bg-elevated rounded p-3 overflow-auto max-h-96 text-kw-text-primary">
          {config ? JSON.stringify(config, null, 2) : "—"}
        </pre>
      </details>

      <div className="space-y-2">
        <h3 className="text-sm uppercase tracking-wide text-kw-text-muted">Secrets</h3>
        <ScopeGate
          require={(g) => canAdminRealm(g, realmId)}
          requires="realm-admin in this realm"
        >
          {displaySecretNames.length === 0 ? (
            <div className="text-xs text-kw-text-faint">No referenced secrets.</div>
          ) : (
            <ul className="text-xs space-y-3">
              {displaySecretNames.map((n) => (
                <SecretRow key={n} realmId={realmId} name={n} />
              ))}
            </ul>
          )}
        </ScopeGate>
      </div>

      <WriteOpsHint title="Realm-Write-Ops" ops={REALMS_WRITE_OPS} />
    </div>
  );
}
